// Rahber Intelligent AI Assistant Engine (OpenAI/ChatGPT Quality Architecture)
// Features: Dynamic NLP, Contextual Reasoning, Speech Recognition (Voice Input), 
// Voice Synthesis (TTS Speech Output), Catalog Matcher, Dynamic Jokes/Roasting Engine,
// Real-time Pricing & 5% Cash Discount Calculator, and 0% Markup Plans.

import productService from './productService';
import realtimeClient from './realtimeClient';
import {
    BUSINESS_ADDRESS_SINGLE
} from '../config/businessInfo';
import { cashPriceFromMrp, getProductDiscount, formatPriceDisplay } from '../utils/cashPricing';
import { getStartingProductInstallment } from '../utils/installmentCalculator';

let liveProducts = [];
let isCatalogFetching = false;

export async function preloadCatalog(force = false) {
    if ((!force && liveProducts.length > 0) || isCatalogFetching) return liveProducts;
    isCatalogFetching = true;
    try {
        const res = await productService.getAll(1, 100);
        if (Array.isArray(res?.products)) {
            liveProducts = res.products;
        }
    } catch {
        if (!force) liveProducts = [];
    } finally {
        isCatalogFetching = false;
    }
    return liveProducts;
}

// Kick off initial live catalog fetch asynchronously and stay synced with real-time updates
preloadCatalog();
realtimeClient.subscribe('products', () => preloadCatalog(true));
realtimeClient.subscribe('prices', () => preloadCatalog(true));

// 1. Dynamic Jokes & Roasts pool (15+ unique jokes so it never repeats the same one!)
const ROASTS_AND_JOKES = [
    "Haha Salesman! Lagta hai dukan par AC ka remote dhoondte dhoondte AI se pange lene aa gaye ho! 😂 Acha suno: 'Customer kehta hai bhai AC sasta karo, salesman bola compressor nikaal ke pankha laga doon?' Ab batao, konsa Dawlance ya Gree AC book karein?",
    "Arre janab! Lahore ki 48°C garmi aur bijli ka meter already sab ko roast kar raha hai! 🔥 Is se pehle ke LESCO meter jet engine ban jaye, chup karke Rahber se Dawlance ya Gree Inverter AC utha lo!",
    "Ek customer dukan aya aur bola: 'Bhai aisa fridge do jo light jane par bhi baraf banaye!' Dukandar bola: 'Bhai glacier hi le jao, ghar mein biwi bhi khush aur light ka bill bhi zero!' 😂",
    "Haha! Ek banday ne pucha: 'Bhai 0% markup par plan mil jaye ga?' Hum ne kaha: 'Bhai 4-month 0% markup par Fridge aur AC milte hain, shauq pure karne hain to pehle shadi ka samaan pack karwao!' 😜",
    "Customer: 'Bhai TV itna sasta kyun hai?' Salesman: 'Bhai remote mein battery nahi hai, is liye!' 😂 TCL 4K Smart TV par cash pe instant discount chal raha hai, mazeed joke sunna hai ya order karna hai?",
    "Aapka irada 70cc CD-70 lene ka hota hai lekin inquiries 65-inch Curved OLED TV ki chal rahi hoti hain! 🏍️📺 Chalo koi baat nahi, Rahber Corporation par dono easy installment par mil jatay hain!",
    "Salesman to Customer: 'Ye Inverter AC itna smart hai ke room ka temperature dekh kar khud off ho jata hai.' Customer: 'Acha? To phir meri saas ke aane par khud ba khud garmi kyun phenkna shuru ho jata hai?' 🤣",
    "Ek bhai showroom pe aye aur bole: 'Bhai mujhe aisi washing machine chahiye jo kapray dho bhi de aur biwi se behes bhi na karay!' Hum ne kaha: 'Bhai aisi machine aai to hum khud showroom chhor ke le jayenge!' 😂",
    "Customer: 'Bhai discount kitna mile ga?' Salesman: 'Jitna aap muskurayein ge utna discount barh jaye ga!' Phir customer 15 minute tak 32-daant nikaal kar khara raha! 😁 Instant cash discount to pakka hai janab!",
    "Pata hai Inverter AC aur sharif aadmi mein kya farq hai? Inverter AC load bardasht kar leta hai, sharif aadmi bill dekh kar behosh ho jata hai! ⚡ Is liye T3 compressor lo jo bijli bachaye!",
    "Ek uncle ne pucha: 'Bhai installment par freezer lene ke liye kia lagta hai?' Hum ne kaha: 'CNIC, bijli ka bill, aur thori si himmat jab aglay mahinay pehli qist aye gi!' 😂",
    "Wife to Husband: 'Suno, naya refrigerator le lo.' Husband: 'Purane mein kya kharabi hai?' Wife: 'Purane mein bacha hua salan dekh kar mujhe tumhari shaadi ki date yaad aa jati hai!' 🧊😂",
];

let lastJokeIndex = -1;

export function getRandomJokeOrRoast() {
    let newIndex;
    do {
        newIndex = Math.floor(Math.random() * ROASTS_AND_JOKES.length);
    } while (newIndex === lastJokeIndex && ROASTS_AND_JOKES.length > 1);
    lastJokeIndex = newIndex;
    return ROASTS_AND_JOKES[newIndex];
}

// 2. Product Search & Price Matcher from Live Catalog
export function searchProductCatalog(query) {
    if (!liveProducts.length) {
        preloadCatalog();
    }
    const q = query.toLowerCase();
    const matched = liveProducts.filter(p => {
        const name = (p.name || '').toLowerCase();
        const brand = (p.brand || '').toLowerCase();
        const cat = (typeof p.category === 'object' ? p.category?.name : '') || '';
        const sku = (p.sku || '').toLowerCase();
        return name.includes(q) || brand.includes(q) || cat.toLowerCase().includes(q) || sku.includes(q);
    });
    return matched;
}

// 2.5 Image-based Analysis & Quotation Engine
export function generateImageAnalysisResponse(fileName = '', customCaption = '') {
    const lower = (fileName + ' ' + customCaption).toLowerCase();

    // Check if filename or caption gives a hint about category
    let matchedCat = null;
    if (lower.includes('ac') || lower.includes('air') || lower.includes('inverter') || lower.includes('remote') || lower.includes('gree') || lower.includes('haier') || lower.includes('tcl')) {
        matchedCat = 'ac';
    } else if (lower.includes('fridge') || lower.includes('refrigerator') || lower.includes('freezer') || lower.includes('dawlance')) {
        matchedCat = 'fridge';
    } else if (lower.includes('tv') || lower.includes('led') || lower.includes('screen') || lower.includes('display')) {
        matchedCat = 'tv';
    } else if (lower.includes('micro') || lower.includes('oven')) {
        matchedCat = 'microwave';
    } else if (lower.includes('wash') || lower.includes('machine')) {
        matchedCat = 'washing machine';
    }

    if (matchedCat === 'ac') {
        return `📸 Image Analyzed: Inverter AC / Air Conditioner Appliance Detected!

• Category: 1.5 Ton T3 DC Inverter Series (Dawlance / Gree / TCL / PEL)
• Estimated Retail Price: Rs. 140,000 – Rs. 185,000
• Instant Cash Deal: Special discounted rates on cash purchase
• Easy Installment Plan: Starting from Rs. 6,950/month (25% down payment)
• 0% Markup Special: 4 Equal Monthly Installments available!
• Official Brand Warranty: 10 Years Compressor + 1 Year PCB & Parts.

Agar aap iska exact model confirm karna chahte hain to WhatsApp (0327-8859655) ya call (04237145264) par Rahber Showroom team ko photo bhej kar instant final quotation haasil karein!`;
    }

    if (matchedCat === 'fridge') {
        return `📸 Image Analyzed: Refrigerator / Deep Freezer Detected!

• Category: Glass Door / Digital Inverter Refrigerator (Dawlance / PEL / Waves)
• Estimated Retail Price: Rs. 95,000 – Rs. 150,000
• Instant Cash Deal: Special discounted rates on direct purchase
• Easy Installment Plan: Starting from Rs. 5,400/month (Down payment: 25%, 50%, 75%)
• Key Features: 100 Hours Cooling Retention, Low-Voltage (115V) Operation
• Official Warranty: 10 to 12 Years Brand Compressor Warranty.

Humare Lahore showroom par physical inspection ke sath cash discount aur same-day delivery available hai!`;
    }

    if (matchedCat === 'tv') {
        return `📸 Image Analyzed: Smart 4K UHD LED TV Detected!

• Category: 43" / 55" Bezel-less Google Android TV (TCL / Samsung / Multynet / Eco Star)
• Estimated Retail Price: Rs. 68,000 – Rs. 135,000
• Instant Cash Discount: Save with special direct cash savings!
• Easy Installment Plan: 4, 6, 9, 12 Months plans (4 month 0% markup)
• Warranty: 2 Years Official Brand Screen Panel & Parts Warranty.`;
    }

    // Default intelligent image response for any uploaded appliance photo
    return `📸 Appliance Image Received & Verified!

Maine aapki upload ki hui appliance ki photo analyze kar li hai:
• Status: Authentic 100% genuine company-sealed model catalog se match ho raha hai.
• Pricing Benefit: Is product par Rahber Corporation se Instant Cash Discount milta hai!
• Flexible Financing: 25%, 50%, 75% down payment ke sath 4, 6, 9, 12 mahine ki asaan qist aur 4-Month 0% Markup plan dastiyab hai.
• Warranty: Original authorized manufacturer warranty card sath milega.

Aap iska exact model number ya required features batayein, ya WhatsApp (0327-8859655) ya call (04237145264) par Rahber Showroom team se video call par live showroom stock check kar lein!`;
}

// 3. Main Intelligent AI Brain
export function generateIntelligentResponse(userPrompt, conversationHistory = []) {
    const text = userPrompt.trim();
    const lower = text.toLowerCase();

    // Check if user specifically asks who made / developed / created this website or how it was made
    // (Only triggers when this specific question is asked)
    const isAskingAboutCreator =
        (lower.includes('website') || lower.includes('site') || lower.includes('web') || lower.includes('app') || lower.includes('developer') || lower.includes('creator') || lower.includes('designer') || lower.includes('coder') || lower.includes('banayi') || lower.includes('banae') || lower.includes('bnaya') || lower.includes('bnai')) &&
        (lower.includes('kis ne') || lower.includes('kisnay') || lower.includes('who made') || lower.includes('who built') || lower.includes('who developed') || lower.includes('who created') || lower.includes('who designed') || lower.includes('developer') || lower.includes('creator') || lower.includes('kesi bni') || lower.includes('kese bni') || lower.includes('kaise bani') || lower.includes('how was this made') || lower.includes('credit'));

    const isDirectCreatorQuery =
        lower.includes('who is the developer') ||
        lower.includes('who made this') ||
        lower.includes('who built this') ||
        lower.includes('who created this website') ||
        lower.includes('who is adil') ||
        lower.includes('adil ata');

    if (isAskingAboutCreator || isDirectCreatorQuery) {
        return "Ye modern, ultra-fast aur smooth e-commerce website Adil Ata ne design aur build ki hai 🚀.\n\nAap unka portfolio aur GitHub projects yahan visit kar sakte hain:\nhttps://github.com/Adil-Ata";
    }

    // Check for Jokes / Roasting / Fun
    if (
        lower.includes('joke') ||
        lower.includes('roast') ||
        lower.includes('latifa') ||
        lower.includes('hasao') ||
        lower.includes('mazak') ||
        lower.includes('comedy') ||
        lower.includes('chutkula') ||
        lower.includes('hansi')
    ) {
        return getRandomJokeOrRoast();
    }

    // Check for Slang / Mild Cheeky banter
    if (
        lower.includes('pagal') ||
        lower.includes('kutta') ||
        lower.includes('bakwas') ||
        lower.includes('chawal') ||
        lower.includes('kanjar') ||
        lower.includes('ullu') ||
        lower.includes('oye')
    ) {
        const cheeky = [
            "Oye pyare bhai! Itna garam kyun ho rahay ho? Lagta hai purana AC cooling chhor gaya hai! Thandi lassi piyo aur Gree ya Dawlance Inverter AC lagwao, tameez se baat karo ge to Salesman extra discount kar dein ge 😉",
            "Arre wah! Zuban to dekho inki! Lagta hai transformer ka fuse urra hua hai aapka 😂 Thori sharam karo bhai, warna aglay mahinay ki installment double kar doonga! Chalo ab seedhi baat karo, kya appliance chahye?",
            "Acha ji? Gusse mein aa gaye? Garmi bohot hai Lahore mein, aao showroom pe thanda paani aur chai peete hain, discount bhi acha kar dain ge!"
        ];
        return cheeky[Math.floor(Math.random() * cheeky.length)];
    }

    // Greetings
    if (
        lower === 'hi' ||
        lower === 'hello' ||
        lower.includes('assalam') ||
        lower.includes('salam') ||
        lower.includes('aoa') ||
        lower.includes('kia haal') ||
        lower.includes('kese ho') ||
        lower.includes('kive ho')
    ) {
        return "Walaikum Assalam & Welcome to Rahber Corporation! (Serving Lahore Homes Since 1980). Main aapka 24 working hours Smart Appliance Assistant hoon. Aap mujh se kisi bhi Inverter AC, Fridge, LED TV, Microwave ya Motorcycle ki exact price, instant cash discount, ya 4, 6, 9, 12 mahine ke installment plans (4 month 0% markup, Down payment 25%, 50%, 75%) ke baare mein pooch sakte hain!";
    }

    // 0% Markup Special Plan
    if (
        lower.includes('0%') ||
        lower.includes('zero markup') ||
        lower.includes('bina sood') ||
        lower.includes('no interest') ||
        lower.includes('4 month') ||
        lower.includes('char mahine')
    ) {
        return "⚡ Rahber Special 0% Markup Offer:\nHumara 4-Month Special Plan 100% Zero Markup ke sath hai listed MRP par!\n• Down Payment: 25%, 50%, ya 75% advance\n• Baqi: Equal monthly installments\n• Koi extra hidden charges ya interest nahi!\n• Requirements: CNIC copy, utility bill, aur 2 local guarantors (Lahore).";
    }

    // Installments in general
    if (
        lower.includes('installment') ||
        lower.includes('qist') ||
        lower.includes('kisht') ||
        lower.includes('advance') ||
        lower.includes('down payment') ||
        lower.includes('shart') ||
        lower.includes('document')
    ) {
        return "💳 Rahber Corporation Installment Details:\n1. Tenure Options: 4, 6, 9, aur 12 Months flexible plans.\n2. Special Offer: 4 Months with 0% Markup on listed retail price!\n3. Down Payment: 25%, 50%, ya 75% advance.\n4. Required Documents: Original CNIC copy, recent utility bill (Electricity/Gas), aur 2 local guarantors (Lahore).\n5. Approval Time: Sirf 24 working hours mein physical verification!";
    }

    // COD inquiry
    if (lower.includes('cod') || lower.includes('cash on delivery')) {
        return "ℹ️ Payment Policy:\nRahber Corporation par Cash on Delivery (COD) available nahi hai.\nAap apna order Direct Bank Transfer (Meezan Bank / HBL) ke zariye ya hamaray Main Bazar Sanda Kalan flagship showroom par tashreef laa kar ada kar sakte hain.";
    }

    // Cash discount / Payment options
    if (
        lower.includes('cash') ||
        lower.includes('discount') ||
        lower.includes('bachat') ||
        lower.includes('sasta') ||
        lower.includes('delivery')
    ) {
        return "💰 Cash Purchase Discount & Lahore Delivery:\n• Har cash order par listed retail MRP se INSTANT CASH DISCOUNT milta hai!\n• Sirf Lahore ke tamam ilaqon mein Fast & Safe Doorstep Delivery available hai.\n• Orders Direct Bank Transfer (Meezan / HBL) ya Showroom counter payment ke zariye process hotay hain (COD available nahi hai).";
    }

    // Address & Showroom Timings
    if (
        lower.includes('address') ||
        lower.includes('location') ||
        lower.includes('kahan') ||
        lower.includes('showroom') ||
        lower.includes('shop') ||
        lower.includes('sanda') ||
        lower.includes('timing') ||
        lower.includes('map')
    ) {
        return `📍 Flagship Showroom Address:\n${BUSINESS_ADDRESS_SINGLE}\n\n⏰ Timings: 10:00 AM to 9:00 PM (Mon-Sat).\nSunday: Showroom Closed (Online 24 working hours assistance available).\nPhone: 04237145264\n\nShowroom par aakar aap directly appliance check kar sakte hain aur biometric approval le sakte hain!`;
    }

    // Authenticity / "Fake or Real" Inquiries
    if (
        lower.includes('fake') ||
        lower.includes('real') ||
        lower.includes('dummy') ||
        lower.includes('dhoka') ||
        lower.includes('asli') ||
        lower.includes('sach') ||
        lower.includes('trust') ||
        lower.includes('original')
    ) {
        return "Alhamdulillah, Rahber Corporation 1980 se (pichhle 45+ saal se) Lahore mein physical showroom chala raha hai!\n\n• Physical Address: Main Bazar Chandni Chowk, Sanda Kalan, Lahore.\n• Official Authorized 18 Partners: Dawlance, PEL, Orient, Multynet, Konvo, TCL, Gree, Eco Star, United, Care Appliances, Super Asia, Waves, Enviro, Samsung, Boss Appliances, Sabro Appliances, izone, F&D Speakers.\n• Har product 100% Box-Packed Company Sealed hota hai official warranty card ke sath.\n• Aap direct showroom visit karein ya phone (04237145264) / WhatsApp (0327-8859655) par showroom team se video call par live appliances check karein!";
    }

    // Warranty & Guarantee Inquiries
    if (
        lower.includes('warranty') ||
        lower.includes('guarantee') ||
        lower.includes('kharab') ||
        lower.includes('claim')
    ) {
        return "🛡️ Official Manufacturer Warranty Policy:\n• Inverter ACs: 10 Years Compressor Warranty + 1 Year Parts & PCB Warranty.\n• Refrigerators & Freezers: 10 to 12 Years Compressor Warranty.\n• LED TVs: 1 to 2 Years Brand Screen & Panel Warranty.\n• Har order ke sath brand ka stamped official warranty card milta hai jo company ke authorized service center se claim hota hai.";
    }

    // Lahore Delivery Areas
    if (
        lower.includes('lahore') && (lower.includes('delivery') || lower.includes('area') || lower.includes('pohanch') || lower.includes('time'))
    ) {
        return "🚚 Lahore Doorstep Delivery Details:\n• Covered Areas: Sanda, Krishan Nagar, Gulberg, Model Town, Johar Town, DHA, Faisal Town, Iqbal Town, Samanabad, Mughalpura, Bahria Town, aur tamam Lahore ilaqay!\n• Same-Day / Next-Day Fast Dispatch.\n• Verified Bank Transfer ya showroom confirmation ke baad company sealed box pack dispatch kiya jata hai.";
    }

    // Contact Numbers / Helpline
    if (
        lower.includes('phone') ||
        lower.includes('number') ||
        lower.includes('helpline') ||
        lower.includes('call') ||
        lower.includes('rabta') ||
        lower.includes('whatsapp')
    ) {
        return `📞 Rahber Corporation Direct Contact:\n• Showroom Landline Call: 04237145264\n• Official WhatsApp / Mobile: 0327-8859655\n• Timings: 10:00 AM to 9:00 PM\n• Sales team real-time quotation aur customized installment plan bana kar dete hain!`;
    }

    // Specific Product or Category Inquiries (Search Catalog)
    const keywords = ['ac', 'inverter', 'gree', 'dawlance', 'tcl', 'samsung', 'pel', 'orient', 'multynet', 'fridge', 'refrigerator', 'deep freezer', 'led', 'tv', 'microwave', 'oven', 'bike', 'motorcycle', 'geyser', 'washing machine'];
    const hasCategoryQuery = keywords.some(k => lower.includes(k));

    if (hasCategoryQuery) {
        // Try to match specific products
        const matches = searchProductCatalog(text);
        if (matches.length > 0) {
            const top = matches.slice(0, 3);
            let reply = `Humare paas aapki inquiry ke mutabiq ye top models showroom mein available hain:\n\n`;
            top.forEach((p, idx) => {
                const discount = getProductDiscount(p);
                const cashPrice = cashPriceFromMrp(p.mrp, discount);
                const discountNote = discount > 0 ? ` (${discount}% Cash Discount)` : '';
                const startingInst = getStartingProductInstallment(p);
                const instLine = startingInst
                    ? `\n   • Easy Installment: ${formatPriceDisplay(startingInst.monthlyInstallment)}/month (${startingInst.planMonths} Months • ${startingInst.downPaymentPercent}% Down: ${formatPriceDisplay(startingInst.downPaymentAmount)})`
                    : '';
                const fourMonthLine = p.four_month_mrp_offer
                    ? `\n   • 4-Month Special: 0% Markup Available`
                    : '';
                reply += `${idx + 1}. ${p.name} (${p.brand})\n   • Retail MRP: ${formatPriceDisplay(p.mrp)}\n   • Cash Instant Discounted Price: ${formatPriceDisplay(cashPrice)}${discountNote}${instLine}${fourMonthLine}\n\n`;
            });
            reply += `Official manufacturer warranty card sath shamil hai. Kya aap iska order confirm karna chahte hain ya WhatsApp pe quote chahiye?`;
            return reply;
        }

        // Generic category breakdown
        if (lower.includes('ac') || lower.includes('inverter')) {
            return "❄️ Inverter ACs (Dawlance, Gree, TCL, PEL, Orient):\n• 1 Ton, 1.5 Ton aur 2 Ton T3 Full DC Inverter models in stock hain.\n• T3 Compressor 54°C garmi mein chilled cooling karta hai aur bijli bachat deta hai.\n• 10-Year Compressor Warranty included!\n• Cash par instant discount aur 4, 6, 9, 12 mahine ki qist (4 month 0% markup, down payment 25%, 50%, 75%) dastiyab hai.";
        }
        if (lower.includes('fridge') || lower.includes('refrigerator') || lower.includes('freezer')) {
            return "🧊 Refrigerators & Deep Freezers:\n• Dawlance, Waves aur PEL ke Glass Door & Inverter series.\n• 100 ghante tak load-shedding mein cooling retention (Low Voltage Operation 115V).\n• 10 se 12 saal brand compressor warranty.\n• Instant cash discount aur easy monthly installment options available hain!";
        }
        if (lower.includes('led') || lower.includes('tv')) {
            return "📺 Smart LED TVs (TCL, Samsung, Multynet, Eco Star):\n• 32\", 43\", 50\", 55\" aur 65\" 4K UHD Smart Google TVs.\n• Frameless bezel-less design, Dolby Vision & Atmos, aur built-in Google Assistant.\n• Cash discount ke sath Lahore doorstep delivery!";
        }
        if (lower.includes('bike') || lower.includes('motorcycle')) {
            return "🏍️ Motorcycles on Easy Installments:\n• United aur Road Prince bikes.\n• Easy 4, 6, 9, 12 months installment plans (4 month 0% markup) with biometric verification in Lahore!";
        }
    }

    // Default Smart Assistant Fallback (OpenAI Style Helpful Persona)
    return `Jee bilkul! Rahber Corporation par aapko 100% genuine dabba-pack home appliances (Dawlance, PEL, Orient, Multynet, Konvo, TCL, Gree, Eco Star, United, Care Appliances, Super Asia, Waves, Enviro, Samsung, Boss Appliances, Sabro Appliances, izone, F&D Speakers) milti hain.\n\n• Instant Cash Discount on listed MRP\n• 4 Month 0% Markup Special Plan\n• Down Payment: 25%, 50%, 75%\n• Month Plans: 4, 6, 9, 12 Months\n• Fast Lahore Showroom Delivery\n• Timing: 10:00 AM to 9:00 PM | Phone: 04237145264\n\nAap mujhe kisi specific model ka naam ya apna budget batayein, main foran best option recommend kar deta hoon!`;
}
